import { Suspense } from "react";
import { NavLink, useNavigate } from "react-router";

import {
  DashboardLayoutEmpty,
  DashboardTaskCard,
  DashboardTaskCardLink,
  DashboardTaskCardTitle,
} from "~components/Dashboard/DashboardLayout";
import { useEmployeeAdministrationTaskBar } from "~components/Resources/EmployeeAdministration/useEmployeeAdministrationTaskBar";
import { usePhotoDocAssignmentTaskBar } from "~components/Resources/PhotoDocumentation/PhotoDocView/desktop/usePhotoDocAssignmentTaskBar.ts";
import { useFailedMobileUploads } from "~components/Resources/PhotoDocumentation/UploadQueue/useFailedMobileUploads.ts";
import { usePhotoDocUploadQueueTaskBar } from "~components/Resources/PhotoDocumentation/UploadQueue/usePhotoDocUploadQueueTaskBar.ts";
import { useUploadQueueVisibility } from "~components/Resources/PhotoDocumentation/UploadQueueVisibilityContext.tsx";
import { useToolAdministrationTaskBar } from "~components/Resources/ToolAdministration/ToolAdministrationDesktop/useToolAdministrationTaskBar.ts";
import { ROUTES } from "~components/routeDefinitions.ts";
import {
  Priority,
  TaskBadge,
  TaskBar,
} from "~components/shared/TaskBar/TaskBar.tsx";
import { useIsDesktop } from "~components/shared/useIsDesktop/useIsDesktop.ts";
import { useTimeApprovalTaskCounts } from "~components/TimeManagement/shared/TimeApprovalTaskBar/useTimeApprovalTaskCounts";
import { useCurrentTenant } from "~contexts/CurrentTenantContext/CurrentTenantContext.tsx";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import { lazyWithRetry } from "~lib/lazyWithRetry.ts";

// The task bars drag the pickers and the tool administration into the dashboard, which is an eager route
const TimeApprovalTaskBar = lazyWithRetry(() =>
  import(
    "~components/TimeManagement/shared/TimeApprovalTaskBar/TimeApprovalTaskBar"
  ).then((m) => ({ default: m.TimeApprovalTaskBar })),
);
const EmployeeAdministrationTaskBar = lazyWithRetry(() =>
  import(
    "~components/Resources/EmployeeAdministration/EmployeeAdministrationTaskBar"
  ).then((m) => ({ default: m.EmployeeAdministrationTaskBar })),
);
const ToolAdministrationTaskBar = lazyWithRetry(() =>
  import(
    "~components/Resources/ToolAdministration/ToolAdministrationDesktop/ToolAdministrationTaskBar.tsx"
  ).then((m) => ({ default: m.ToolAdministrationTaskBar })),
);

export function DashboardTaskCards({
  showEmptyInfo,
}: {
  showEmptyInfo: boolean;
}) {
  const t = useTranslation();
  const { hasTasks: hasTimeApprovalTasks } = useTimeApprovalTaskCounts();
  const { hasAmbigousAddresses, hasMissingSuperiors } =
    useEmployeeAdministrationTaskBar();
  const { hasTasks: hasToolTasks } = useToolAdministrationTaskBar();
  const isDesktop = useIsDesktop();
  const { failedCount: localFailedCount } = usePhotoDocUploadQueueTaskBar();
  const { count: mobileFailedCount } = useFailedMobileUploads();
  const {
    hasTasks: hasPhotoDocAssignmentTasks,
    pendingCount: photoDocPendingCount,
  } = usePhotoDocAssignmentTaskBar({ enabled: isDesktop });

  // On mobile, mobileFailedCount reflects the same local failures → avoid double counting.
  const totalUploadFailedCount = isDesktop
    ? localFailedCount + mobileFailedCount
    : localFailedCount;
  const showUploadQueueTask = totalUploadFailedCount > 0;

  const showPhotoDocAssignmentTask = isDesktop && hasPhotoDocAssignmentTasks;

  if (
    hasTimeApprovalTasks ||
    hasAmbigousAddresses ||
    hasMissingSuperiors ||
    hasToolTasks ||
    showUploadQueueTask ||
    showPhotoDocAssignmentTask
  ) {
    return (
      <>
        <h2>{t.tasks}</h2>
        {hasTimeApprovalTasks && <TimeApprovalTaskCard />}
        {hasMissingSuperiors && (
          <EmployeeAdministrationMissingSuperiorsTaskCard />
        )}
        {hasAmbigousAddresses && (
          <EmployeeAdministrationAmbiguousAddressesTaskCard />
        )}
        {hasToolTasks && <ToolAdministrationTaskCard />}
        {showUploadQueueTask && (
          <UploadQueueTaskCard failedCount={totalUploadFailedCount} />
        )}
        {showPhotoDocAssignmentTask && (
          <PhotoDocAssignmentTaskCard pendingCount={photoDocPendingCount} />
        )}
      </>
    );
  }
  if (showEmptyInfo) {
    return (
      <>
        <h2>{t.tasks}</h2>
        <DashboardLayoutEmpty message={t.tasksEmpty} />
      </>
    );
  }
  return null;
}

function TimeApprovalTaskCard() {
  const t = useTranslation();
  const { number: tenantNumber } = useCurrentTenant();

  return (
    <DashboardTaskCard>
      <DashboardTaskCardTitle
        // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted i18n translation string, no user input
        dangerouslySetInnerHTML={{
          __html: t.tasksTimeApprovalTitleHtml,
        }}
      />
      <Suspense fallback={null}>
        <TimeApprovalTaskBar variant="vertical" />
      </Suspense>
      <NavLink to={ROUTES.timeApproval.link({ tenantNumber })}>
        <DashboardTaskCardLink>{t.tasksTimeApprovalLink}</DashboardTaskCardLink>
      </NavLink>
    </DashboardTaskCard>
  );
}

function EmployeeAdministrationMissingSuperiorsTaskCard() {
  const t = useTranslation();
  const { number: tenantNumber } = useCurrentTenant();

  return (
    <DashboardTaskCard>
      <DashboardTaskCardTitle
        // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted i18n translation string, no user input
        dangerouslySetInnerHTML={{
          __html: t.tasksEmployeeAdministrationMissingSuperiorsTitleHtml,
        }}
      />
      <Suspense fallback={null}>
        <EmployeeAdministrationTaskBar
          variant="vertical"
          limitTo="superiorMissing"
        />
      </Suspense>
      <NavLink to={ROUTES.employees.link({ tenantNumber })}>
        <DashboardTaskCardLink>
          {t.tasksEmployeeAdministrationLink}
        </DashboardTaskCardLink>
      </NavLink>
    </DashboardTaskCard>
  );
}

function EmployeeAdministrationAmbiguousAddressesTaskCard() {
  const t = useTranslation();
  const { number: tenantNumber } = useCurrentTenant();

  return (
    <DashboardTaskCard>
      <DashboardTaskCardTitle
        // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted i18n translation string, no user input
        dangerouslySetInnerHTML={{
          __html: t.tasksEmployeeAdministrationAmbiguousAddressesTitleHtml,
        }}
      />
      <Suspense fallback={null}>
        <EmployeeAdministrationTaskBar
          variant="vertical"
          limitTo="ambiguousAddress"
        />
      </Suspense>
      <NavLink to={ROUTES.employees.link({ tenantNumber })}>
        <DashboardTaskCardLink>
          {t.tasksEmployeeAdministrationLink}
        </DashboardTaskCardLink>
      </NavLink>
    </DashboardTaskCard>
  );
}

function ToolAdministrationTaskCard() {
  const t = useTranslation();
  const { number: tenantNumber } = useCurrentTenant();

  return (
    <DashboardTaskCard>
      <DashboardTaskCardTitle
        // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted i18n translation string, no user input
        dangerouslySetInnerHTML={{
          __html: t.tasksToolAdministrationTitleHtml,
        }}
      />
      <Suspense fallback={null}>
        <ToolAdministrationTaskBar variant="vertical" />
      </Suspense>
      <NavLink to={ROUTES.tools.link({ tenantNumber })}>
        <DashboardTaskCardLink>
          {t.tasksToolAdministrationLink}
        </DashboardTaskCardLink>
      </NavLink>
    </DashboardTaskCard>
  );
}

function UploadQueueTaskCard({ failedCount }: { failedCount: number }) {
  const t = useTranslation();
  const { setQueueVisible } = useUploadQueueVisibility();
  const { number: tenantNumber } = useCurrentTenant();
  const navigate = useNavigate();

  const handleBadgeClick = () => {
    setQueueVisible(true);
    navigate(ROUTES.photoDocumentationView.link({ tenantNumber }));
  };

  return (
    <DashboardTaskCard>
      <DashboardTaskCardTitle
        // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted i18n translation string, no user input
        dangerouslySetInnerHTML={{ __html: t.tasksUploadQueueTitleHtml }}
      />
      <TaskBar variant="vertical">
        <TaskBadge
          count={failedCount}
          text={t.tasksUploadQueueBadgeText}
          onClick={handleBadgeClick}
          priority={Priority.HIGH}
        />
      </TaskBar>
      <NavLink to={ROUTES.photoDocumentationView.link({ tenantNumber })}>
        <DashboardTaskCardLink>{t.tasksUploadQueueLink}</DashboardTaskCardLink>
      </NavLink>
    </DashboardTaskCard>
  );
}

function PhotoDocAssignmentTaskCard({
  pendingCount,
}: {
  pendingCount: number;
}) {
  const t = useTranslation();
  const { number: tenantNumber } = useCurrentTenant();
  const navigate = useNavigate();

  const handleBadgeClick = () => {
    navigate(
      `${ROUTES.photoDocumentationView.link({ tenantNumber })}?expandManagerSection=true`,
    );
  };

  return (
    <DashboardTaskCard>
      <DashboardTaskCardTitle
        // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted i18n translation string, no user input
        dangerouslySetInnerHTML={{
          __html: t.tasksPhotoDocAssignmentTitleHtml,
        }}
      />
      <TaskBar variant="vertical">
        <TaskBadge
          count={pendingCount}
          text={t.tasksPhotoDocAssignmentBadgeText}
          onClick={handleBadgeClick}
          priority={Priority.MEDIUM}
        />
      </TaskBar>
      <NavLink to={ROUTES.photoDocumentationView.link({ tenantNumber })}>
        <DashboardTaskCardLink>
          {t.tasksPhotoDocAssignmentLink}
        </DashboardTaskCardLink>
      </NavLink>
    </DashboardTaskCard>
  );
}
